'use client'

import { useRef, useState, useSyncExternalStore } from 'react'
import { Button } from '@elmnts/ui/button'
import { useNotificationActions } from '@/hooks/notifications/useNotificationActions'
import { cn } from '@/lib/utils'
import type { NotificationItem } from '@/services/notifications/types'
import { Notification } from '../Notification'
import { defaultRenderer } from './default'
import {
  FEATURE_ANNOUNCEMENTS,
  type FeatureAnnouncement,
} from './feature-announcements'
import type { NotificationRenderer, NotificationViewProps } from './types'

/** Mesmo slug que o back valida (`feature_announcement.py`). */
const FEATURE_KEY = /^[a-z0-9]+(-[a-z0-9]+)*$/
const FEATURE_KEY_MAX = 64

/**
 * O `context` é `dict[str, Any]` no back. Só a `feature_key` é lida, e ela só
 * escolhe uma entrada do catálogo local — nenhum outro campo do context chega
 * à tela. `Object.hasOwn` barra chave do protótipo (`constructor` casa o slug).
 */
function readContext(notification: NotificationItem): FeatureAnnouncement | null {
  const key = (notification.context as { feature_key?: unknown } | undefined)?.feature_key
  if (typeof key !== 'string' || key.length > FEATURE_KEY_MAX || !FEATURE_KEY.test(key)) return null
  return Object.hasOwn(FEATURE_ANNOUNCEMENTS, key) ? FEATURE_ANNOUNCEMENTS[key] : null
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {}
  const mql = window.matchMedia(REDUCED_MOTION)
  mql.addEventListener('change', onChange)
  return () => mql.removeEventListener('change', onChange)
}

function getReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches
}

function usePrefersReducedMotion(): boolean {
  // No servidor assume movimento reduzido: o primeiro paint é o poster, nunca um
  // vídeo que precisaria sumir na hidratação.
  return useSyncExternalStore(subscribeReducedMotion, getReducedMotion, () => true)
}

/** Índice do passo em vigor no instante `t` (último com `start <= t`). */
export function stepAt(steps: readonly { start: number }[], t: number): number {
  let current = 0
  steps.forEach((s, i) => {
    if (s.start <= t) current = i
  })
  return current
}

function PreviewCard(props: NotificationViewProps) {
  // Mesma forma do default (título + body em 1 linha); o conteúdo rico é só do Full.
  return <defaultRenderer.Preview {...props} />
}

function FullView(props: NotificationViewProps) {
  const announcement = readContext(props.notification)
  if (!announcement) return <defaultRenderer.Full {...props} />
  return <Walkthrough {...props} announcement={announcement} />
}

/**
 * UM vídeo contínuo com o fluxo inteiro + a lista de passos ACIMA dele como
 * legenda sincronizada: o passo em vigor (pelo `currentTime`) fica destacado, e
 * clicar num passo leva o vídeo ao início dele. O `body` NÃO aparece aqui: ele é
 * o fallback do sino, do toast e da notificação nativa, e repetiria os passos.
 */
function Walkthrough({
  notification,
  announcement,
  showReadAction = true,
  onMarkedRead,
}: NotificationViewProps & { announcement: FeatureAnnouncement }) {
  const { markRead } = useNotificationActions()
  const reduce = usePrefersReducedMotion()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [current, setCurrent] = useState(0)
  const { media, steps } = announcement

  const play = () => {
    // `play()` rejeita se o browser barrar (aba em segundo plano etc.): o vídeo
    // só fica parado no quadro atual, não há o que tratar.
    void videoRef.current?.play()?.catch(() => {})
  }

  const goTo = (i: number) => {
    const video = videoRef.current
    if (video) video.currentTime = steps[i].start
    setCurrent(i)
    if (!reduce) play()
  }

  return (
    <Notification notification={notification}>
      <article className="flex flex-col gap-3">
        {/* `pr-8`: o X do Dialog fica no canto superior direito. */}
        <header className="flex items-center gap-2 pr-8">
          <Notification.KindIcon />
          <Notification.KindBadge />
          <Notification.Title as="h1" className="min-w-0 text-lg font-semibold tracking-tight" />
        </header>

        <ol aria-label="Como usar" className="space-y-1">
          {steps.map((step, i) => {
            const active = i === current
            return (
              <li key={step.start}>
                <button
                  type="button"
                  aria-current={active ? 'step' : undefined}
                  onClick={() => goTo(i)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-2 py-0.5 text-left text-base transition-colors',
                    active ? 'bg-primary/10 font-medium text-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                      active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {i + 1}
                  </span>
                  <span className="min-w-0">{step.text}</span>
                </button>
              </li>
            )
          })}
        </ol>

        {/* Hover pausa: dá tempo de ler o quadro. Sob movimento reduzido nada toca sozinho. */}
        <div
          className="aspect-[16/10] w-full overflow-hidden rounded-lg border border-border bg-muted/30"
          onMouseEnter={() => videoRef.current?.pause()}
          onMouseLeave={() => {
            if (!reduce) play()
          }}
        >
          <video
            ref={videoRef}
            autoPlay={!reduce}
            loop={!reduce}
            controls={reduce}
            muted
            playsInline
            preload={reduce ? 'metadata' : 'auto'}
            poster={media.poster}
            aria-label={media.alt}
            onTimeUpdate={(e) => setCurrent(stepAt(steps, e.currentTarget.currentTime))}
            className="size-full object-cover"
          >
            <source src={media.src} type="video/webm" />
          </video>
        </div>

        {showReadAction && (
          <footer className="flex justify-end">
            <Button
              type="button"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate({ ids: [notification.id] }, { onSuccess: onMarkedRead })}
            >
              Entendi
            </Button>
          </footer>
        )}
      </article>
    </Notification>
  )
}

export const featureAnnouncementRenderer: NotificationRenderer = {
  Preview: PreviewCard,
  Full: FullView,
}
